// src/services/ticketService.ts
import api from '../api/apiClient';
import { AxiosError } from 'axios';

export interface Ticket {
  _id: string;
  ticketNumber: string;
  relatedUser: string | any;
  relatedConversation?: string;
  relatedAppointment?: string;
  title: string;
  description: string;
  category: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  status: 'open' | 'in-progress' | 'pending-customer' | 'resolved' | 'closed';
  templateId?: string;
  assignedTo?: string | any;
  createdBy: string | any;
  resolution?: string;
  resolvedAt?: string;
  resolvedBy?: string | any;
  internalNotes?: Array<{
    note: string;
    addedBy: string | any;
    addedAt: string;
  }>;
  tags: string[];
  sla?: {
    firstResponseTarget?: string;
    resolutionTarget?: string;
    firstResponseAt?: string;
    resolutionAt?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface TicketTemplate {
  id: string;
  name: string;
  category: string;
  priority: string;
  title: string;
  description: string;
  tags: string[];
}

export interface CreateTicketData {
  relatedUser: string;
  relatedConversation?: string;
  relatedAppointment?: string;
  title?: string;
  description?: string;
  category?: string;
  priority?: 'low' | 'medium' | 'high' | 'critical';
  templateId?: string;
  tags?: string[];
  details?: string;
}

/**
 * Get all available ticket templates
 */
export async function getTicketTemplates(): Promise<TicketTemplate[]> {
  try {
    const response = await api.getWithParams<{ data: TicketTemplate[] }>('/tickets/templates');
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error fetching ticket templates:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Create a new ticket
 */
export async function createTicket(data: CreateTicketData): Promise<Ticket> {
  try {
    const response = await api.postWithData<{ data: Ticket }>('/tickets', data);
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error creating ticket:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Get all tickets (with optional filters)
 */
export async function getTickets(params?: {
  status?: string;
  priority?: string;
  category?: string;
  assignedTo?: string;
  relatedUser?: string;
  search?: string;
}): Promise<Ticket[]> {
  try {
    const response = await api.getWithParams<{ data: Ticket[] }>('/tickets', params);
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error fetching tickets:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Get a single ticket by ID
 */
export async function getTicket(ticketId: string): Promise<Ticket> {
  try {
    const response = await api.getWithParams<{ data: Ticket }>(`/tickets/${ticketId}`);
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error fetching ticket:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Update a ticket
 */
export async function updateTicket(
  ticketId: string,
  data: {
    status?: string;
    priority?: string;
    assignedTo?: string;
    resolution?: string;
    tags?: string[];
  }
): Promise<Ticket> {
  try {
    const response = await api.putWithData<{ data: Ticket }>(`/tickets/${ticketId}`, data);
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error updating ticket:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Add internal note to ticket
 */
export async function addTicketNote(ticketId: string, note: string): Promise<Ticket> {
  try {
    const response = await api.postWithData<{ data: Ticket }>(`/tickets/${ticketId}/notes`, { note });
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Error adding ticket note:', err.response?.data || err.message);
    throw err;
  }
}

